/**
 * TextField — the design system's labelled text input: pill-shaped 1.5px-border
 * wrap, optional start/end icons, required asterisk, helper/error line, and a
 * shake on every failed submit.
 *
 * Ported 1:1 from `tw-components.jsx` (grep `function TextField`). Deviations are
 * mechanical only: `cx` and `Icon` are imported, and `./keyframes` is imported for
 * the `dndShake` keyframe + `.dnd-shake` rule (upstream declares them in
 * `Dens N Dente Tailwind.dc.html`'s own <style>; grep `dndShake`).
 *
 * Two behaviours that are easy to break:
 *
 * - **The shake is imperative on purpose** (remove class → force reflow → re-add).
 *   It fires on the rising edge of `error` and again whenever the caller bumps
 *   `errorNonce` — one bump per submit attempt — so re-submitting a still-invalid
 *   form shakes again. Upstream's comment: a React-state version races, the effect
 *   cleanup can cancel the restart before it paints.
 *
 * - **The input's own `onBlur` is taken** by the focus ring. To validate on blur,
 *   wrap the field and rely on React's bubbling blur, exactly as the reference
 *   checkout does: `<div onBlur={validate}><TextField … /></div>`.
 *
 * `error` doubles as the message: pass a string to show it in the helper slot
 * (truthy non-strings fall back to `helperText`). `...rest` reaches the `<input>`,
 * so `name`, `autoComplete`, `maxLength` etc. all work.
 *
 * **Not a replacement for `common/ui/InputField`.** Existing forms stay on
 * InputField (26+ call sites, event-shaped onChange, checkbox branch); this is the
 * DS control for surfaces being restyled to the reference — the checkout's
 * address forms first.
 */
import React from "react";

import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import "./keyframes";

const FIELD_H = {
  sm: "h-[var(--control-height-sm)]",
  md: "h-[var(--control-height-md)]",
  lg: "h-[var(--control-height-lg)]",
};

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  size = "md",
  startIcon,
  endIcon,
  helperText,
  error,
  errorNonce = 0,
  required = false,
  disabled = false,
  id,
  style = {},
  className = "",
  ...rest
}) {
  const [focused, setFocused] = React.useState(false);
  const reactId = React.useId();
  const fieldId = id || reactId;
  const isError = Boolean(error);
  const msg = isError
    ? typeof error === "string"
      ? error
      : helperText
    : helperText;
  const wrapRef = React.useRef(null);
  const firstRun = React.useRef(true);
  React.useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      if (!isError) return undefined;
    }
    const el = wrapRef.current;
    if (!el || !isError) return undefined;
    el.classList.remove("dnd-shake");
    void el.offsetWidth;
    el.classList.add("dnd-shake");
    const off = setTimeout(() => el.classList.remove("dnd-shake"), 470);
    return () => clearTimeout(off);
  }, [isError, errorNonce]);
  return (
    <div
      className={cx(
        "dnd-field flex flex-col gap-[var(--space-2)] font-sans",
        className
      )}
      style={style}
    >
      {label && (
        <label className="text-sm font-semibold text-ink" htmlFor={fieldId}>
          {label}
          {required && <span className="text-orange-600 ml-[2px]">*</span>}
        </label>
      )}
      <div
        ref={wrapRef}
        className={cx(
          "dnd-field__wrap flex items-center gap-[var(--space-2)] bg-card border-[1.5px] rounded-[var(--radius-pill)] px-[var(--space-5)]",
          "[transition:border-color_var(--duration-fast)_var(--ease-standard),box-shadow_var(--duration-base)_var(--ease-out),background_var(--duration-fast)]",
          FIELD_H[size] || FIELD_H.md,
          isError
            ? "border-error-500"
            : focused
              ? "border-line-strong"
              : "border-line hover:border-line-strong",
          focused && "shadow-md",
          disabled && "bg-neutral-100 cursor-not-allowed"
        )}
      >
        {startIcon && (
          <span
            className={cx("inline-flex", focused ? "text-ink-2" : "text-ink-3")}
          >
            {typeof startIcon === "string" ? (
              <Icon name={startIcon} size={20} weight={400} />
            ) : (
              startIcon
            )}
          </span>
        )}
        <input
          id={fieldId}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-invalid={isError || undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="flex-1 min-w-0 border-0 outline-none bg-transparent font-sans text-base text-ink h-full placeholder:text-ink-3 disabled:cursor-not-allowed disabled:text-[var(--text-disabled)]"
          {...rest}
        />
        {endIcon && (
          <span
            className={cx("inline-flex", focused ? "text-ink-2" : "text-ink-3")}
          >
            {typeof endIcon === "string" ? (
              <Icon name={endIcon} size={20} weight={400} />
            ) : (
              endIcon
            )}
          </span>
        )}
      </div>
      {msg && (
        <span
          className={cx(
            "text-xs flex items-center gap-[var(--space-1)]",
            isError ? "text-error-600" : "text-ink-2"
          )}
        >
          {isError && <Icon name="error" size={14} weight={500} fill />}
          {msg}
        </span>
      )}
    </div>
  );
}
